import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { common, layouts } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import { Skeleton } from 'components/common'
import { SkeletonVariant } from 'components/common/Skeleton'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const ProgramBuilder: React.FC = () => {
  const programBuilderRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()
  const { simulatorDashboardForAdministratorsAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')

  useEffect(() => {
    const exitProgramBuilder = (e: MessageEvent) => {
      if (e.data.type === 'EXIT_SSC') {
        if (simulatorDashboardForAdministratorsAvailable) {
          dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
        } else {
          dispatch(redirectToSta())
        }
      }
    }
    window.addEventListener('message', exitProgramBuilder)
    const removeUnauthorizedListener = handleUnauthorizedEvent(dispatch)

    return () => {
      window.removeEventListener('message', exitProgramBuilder)
      removeUnauthorizedListener()
    }
  }, [dispatch, simulatorDashboardForAdministratorsAvailable])

  const sendTokenAndUserInfo = () => {
    if (programBuilderRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      programBuilderRef.current.contentWindow?.postMessage(
        {
          type: 'SSC',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
          permissions: EnvironmentUtils.getPermissions('selfServiceConfiguration'),
        },
        window.cc.programBuilder ?? ''
      )
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    if (simulatorDashboardForAdministratorsAvailable) {
      dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
    } else {
      dispatch(redirectToSta())
    }
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <iframe
            title='Programs'
            src={`${window.cc.programBuilder}/program-builder`}
            width='100%'
            height='99%'
            ref={programBuilderRef}
            onLoad={sendTokenAndUserInfo}
            style={{ border: 'none' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default ProgramBuilder
